import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { environment } from '@cpd-app/environment';
import { AuthSelectors } from '@cpd-app/state/auth';
import { cleanFragment, describeClick, describeRoute } from './ui-tracker.util';

/**
 * ANALYTICS.25 -- one listener for every click and page view in the app.
 *
 * Nothing is wired per button. A capture-phase click listener on the document
 * sees every click on an interactive element, names it by its component and
 * label (ui-tracker.util.ts), and the router tells us every page view. A
 * feature shipped tomorrow is tracked the moment it lands.
 *
 * SENT WITH fetch, NOT APOLLO, and that is load-bearing. Apollo requests go
 * through SessionInterceptor, which on a failure navigates to an error page or
 * runs a token refresh, and through the page-wide spinner. Analytics must never
 * be able to do any of those to a learner. A plain fetch fails quietly.
 *
 * Runs outside the Angular zone, so a click or a timer here never triggers
 * change detection.
 *
 * Spec: .specify/specs/ANALYTICS.25-click-and-page-view-capture.md
 */

export const RECORD_UI_EVENTS = `
  mutation RecordUiEvents($sessionKey: String!, $events: [UiEventInput!]!) {
    recordUiEvents(sessionKey: $sessionKey, events: $events)
  }
`;

/** Flush when this many are queued, or on the timer, whichever first. */
export const FLUSH_AT = 25;
export const FLUSH_EVERY_MS = 10_000;
/** Events waiting for a token are capped; the oldest are dropped. */
export const MAX_QUEUE = 200;
/** A navigation this soon after a click is credited to the click. */
export const CLICK_CAUSES_NAV_MS = 1500;

const SESSION_KEY_STORAGE = 'cpd.uiTracker.sessionKey';
const SEQ_STORAGE = 'cpd.uiTracker.seq';

export interface QueuedUiEvent {
  kind: 'click' | 'view';
  seq: number;
  at: number;
  route: string;
  subject: string | null;
  fragment: string | null;
  area?: string | null;
  target?: string | null;
  element?: string | null;
  href?: string | null;
  navTrigger?: 'click' | 'back_forward' | 'direct';
  causeSeq?: number | null;
}

function newSessionKey(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // RFC 4122 v4 shape from Math.random, for the rare browser without it.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** sessionStorage can throw (private windows, blocked storage). */
function readStorage(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStorage(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Carry on in memory.
  }
}

@Injectable({ providedIn: 'root' })
export class UiTrackerService implements OnDestroy {
  private started = false;
  private queue: QueuedUiEvent[] = [];
  private token: string | null = null;
  private shareToken: string | null = null;
  private initializing = true;
  private sessionKey = '';
  private seq = 0;
  private lastClick: { seq: number; at: number } | null = null;
  private pendingTrigger: 'imperative' | 'popstate' | 'hashchange' | null =
    null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly subscriptions = new Subscription();
  private readonly teardown: (() => void)[] = [];

  public constructor(
    private readonly router: Router,
    private readonly store: Store,
    private readonly zone: NgZone
  ) {}

  /** Idempotent. Called once from AppComponent. Never throws. */
  public start(): void {
    if (
      this.started ||
      typeof document === 'undefined' ||
      !environment.graphQlUri
    ) {
      return;
    }
    this.started = true;
    try {
      this.sessionKey = readStorage(SESSION_KEY_STORAGE) ?? newSessionKey();
      writeStorage(SESSION_KEY_STORAGE, this.sessionKey);
      this.seq = Number(readStorage(SEQ_STORAGE)) || 0;

      this.subscriptions.add(
        this.store
          .select(AuthSelectors.selectAuthToken)
          .subscribe((t) => (this.token = t))
      );
      this.subscriptions.add(
        this.store
          .select(AuthSelectors.selectAuthSharedToken)
          .subscribe((t) => {
            this.shareToken = t;
            // A counselor reading a shared report: nothing of theirs belongs
            // to the member, so nothing already queued is sent either.
            if (t) {
              this.queue = [];
            }
          })
      );
      this.subscriptions.add(
        this.store
          .select(AuthSelectors.selectIsSessionInitializing)
          .subscribe((i) => (this.initializing = !!i))
      );
      this.subscriptions.add(
        this.router.events.subscribe((event) => {
          if (event instanceof NavigationStart) {
            this.pendingTrigger = event.navigationTrigger ?? null;
          } else if (event instanceof NavigationEnd) {
            this.onNavigationEnd(event);
          }
        })
      );

      this.zone.runOutsideAngular(() => {
        const onClick = (e: MouseEvent) => this.onClick(e);
        const onHide = () => {
          if (document.visibilityState === 'hidden') {
            this.flush(true);
          }
        };
        const onPageHide = () => this.flush(true);
        // Capture phase: a component that stops propagation still counts.
        document.addEventListener('click', onClick, {
          capture: true,
          passive: true
        });
        document.addEventListener('visibilitychange', onHide);
        window.addEventListener('pagehide', onPageHide);
        this.timer = setInterval(() => this.flush(false), FLUSH_EVERY_MS);
        this.teardown.push(
          () =>
            document.removeEventListener('click', onClick, { capture: true }),
          () => document.removeEventListener('visibilitychange', onHide),
          () => window.removeEventListener('pagehide', onPageHide)
        );
      });
    } catch (error) {
      console.error('[ui-tracker] could not start', error);
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.teardown.forEach((undo) => undo());
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  private nextSeq(): number {
    this.seq += 1;
    writeStorage(SEQ_STORAGE, String(this.seq));
    return this.seq;
  }

  private currentPage(): {
    route: string;
    subject: string | null;
    fragment: string | null;
  } {
    const { route, subject } = describeRoute(
      this.router.routerState.snapshot.root
    );
    const fragment = cleanFragment(
      this.router.parseUrl(this.router.url).fragment
    );
    return { route, subject, fragment };
  }

  private onClick(event: MouseEvent): void {
    try {
      const described = describeClick(event.target);
      if (!described) {
        return;
      }
      const seq = this.nextSeq();
      const at = Date.now();
      this.lastClick = { seq, at };
      this.enqueue({
        kind: 'click',
        seq,
        at,
        ...this.currentPage(),
        ...described
      });
    } catch {
      // Never let measurement break a click.
    }
  }

  private onNavigationEnd(event: NavigationEnd): void {
    try {
      const at = Date.now();
      const clicked =
        this.lastClick && at - this.lastClick.at <= CLICK_CAUSES_NAV_MS
          ? this.lastClick
          : null;
      const navTrigger =
        this.pendingTrigger === 'popstate'
          ? 'back_forward'
          : clicked
            ? 'click'
            : 'direct';
      const { route, subject } = describeRoute(
        this.router.routerState.snapshot.root
      );
      const fragment = cleanFragment(
        this.router.parseUrl(event.urlAfterRedirects).fragment
      );
      this.enqueue({
        kind: 'view',
        seq: this.nextSeq(),
        at,
        route,
        subject,
        fragment,
        navTrigger,
        causeSeq: navTrigger === 'click' ? clicked?.seq ?? null : null
      });
      this.pendingTrigger = null;
    } catch {
      // Same promise as onClick.
    }
  }

  private enqueue(event: QueuedUiEvent): void {
    if (this.shareToken) {
      return;
    }
    this.queue.push(event);
    if (this.queue.length > MAX_QUEUE) {
      this.queue.splice(0, this.queue.length - MAX_QUEUE);
    }
    if (this.queue.length >= FLUSH_AT) {
      this.flush(false);
    }
  }

  /**
   * Send what is queued, if there is somewhere to send it. Fire and forget:
   * a failed batch is dropped, never retried, never surfaced.
   *
   * `leaving` is the tab closing or going to the background; keepalive lets
   * the request outlive the page.
   */
  public flush(leaving: boolean): void {
    if (
      !this.queue.length ||
      !this.token ||
      this.shareToken ||
      this.initializing
    ) {
      return;
    }
    const events = this.queue.splice(0, 50);
    const body = JSON.stringify({
      operationName: 'RecordUiEvents',
      query: RECORD_UI_EVENTS,
      variables: { sessionKey: this.sessionKey, events }
    });
    try {
      void fetch(environment.graphQlUri, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`
        },
        body,
        // Browsers cap keepalive bodies at 64 KB; 50 events is well under.
        keepalive: leaving,
        credentials: 'omit'
      }).catch(() => undefined);
    } catch {
      // fetch itself can throw synchronously on a bad init; drop the batch.
    }
    // More than one batch waiting (the cap is 200): send the rest the same way.
    if (this.queue.length) {
      this.flush(leaving);
    }
  }
}
